import React from 'react';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { TABLE_STYLES } from '../../constants/tableStyles';

export type CertificationAnalysisChartType =
  | 'byProvider'
  | 'byLevels'
  | 'byTypes'
  | 'topCertifications'
  | 'expiringCertifications'
  | 'activeCertifications';

export interface CertificationAnalysisChartTypeSelectProps {
  value: CertificationAnalysisChartType;
  onChange: (event: SelectChangeEvent<string>) => void;
}

/**
 * Chart type selector for Certification Analysis (shared across all analysis views).
 */
export function CertificationAnalysisChartTypeSelect({
  value,
  onChange,
}: CertificationAnalysisChartTypeSelectProps) {
  return (
    <FormControl size="small" sx={{ minWidth: 200, maxWidth: 280, flexShrink: 0 }}>
      <Select value={value} onChange={onChange} sx={{ fontSize: '0.875rem' }}>
        <MenuItem value="byProvider">By Service Provider</MenuItem>
        <MenuItem value="byLevels">Certification Levels & Types</MenuItem>
        <MenuItem value="topCertifications">Top Certifications</MenuItem>
        <MenuItem value="expiringCertifications">Expiring Certifications(120d)</MenuItem>
        <MenuItem value="activeCertifications">Active Certifications</MenuItem>
      </Select>
    </FormControl>
  );
}

export interface CertificationAnalysisViewToolbarProps {
  /** Chart type dropdown (required). */
  chartSelect: React.ReactNode;
  /** Optional search field — when set, appears on the same row as the select (left, flex). */
  searchField?: React.ReactNode;
  /** Clear filters + Filter columns (same horizontal padding as other table toolbars). */
  toolbarActions?: React.ReactNode;
}

/**
 * Top bar for certification analysis content: optional search (left) + actions + chart type (right).
 */
export function CertificationAnalysisViewToolbar({
  chartSelect,
  searchField,
  toolbarActions,
}: CertificationAnalysisViewToolbarProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        minHeight: 40,
        px: 2,
        ...TABLE_STYLES.tableToolbarTop,
        mb: 1,
      }}
    >
      {searchField ? (
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 400 }}>{searchField}</Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }} aria-hidden />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          flexWrap: 'wrap',
          ml: 'auto',
        }}
      >
        {toolbarActions}
        {chartSelect}
      </Box>
    </Box>
  );
}
